import dayjs from 'dayjs'
import Image from 'next/image'
import React, { useEffect, useRef, useState } from 'react'

import CalendarIcon from '../../../assets/icons/calendar.svg'
import CommentIcon from '../../../assets/icons/comment.svg'
import { BlogPostConfig, GhostPost } from '../../../utils/types'
import Link from '../../common/Link/Link'
import BlogBanner from '../../widgets/ContentsTool/BlogBanner'
import ContentsTool from '../../widgets/ContentsTool/ContentsTool'
import GuideFooter from '../../widgets/GuideFooter/GuideFooter'
import GuideHeader from '../../widgets/GuideHeader/GuideHeader'
import GuideNavigator from '../../widgets/GuideNavigator/GuideNavigator'
import IconsLoader from '../../widgets/IconsLoader/IconsLoader'
import ReadProgress from '../../widgets/ReadProgress/ReadProgress'
import styles from './BlogPostContainer.module.scss'

type Props = {
  post: GhostPost
  scrollPos: number
  postConfig: BlogPostConfig
}

const BlogPostContainer: React.FC<Props> = ({ post, postConfig, scrollPos }) => {
  const contentRef = useRef<HTMLDivElement>(null)

  const [guideHeight, setGuideHeight] = useState(0)

  useEffect(() => {
    const links = document.getElementsByTagName('a')

    for (let i = 0; i < links.length; i++) {
      const link = links[i]
      if (link.href.includes(window.location.hostname)) continue

      links[i].setAttribute('target', '_blank')
      links[i].setAttribute('rel', 'noopener noreferrer')
      links[i].setAttribute('aria-label', link.href)
    }
  }, [])

  // subtract guide header for proper calculation
  const calculatedScrollPos = scrollPos - guideHeight
  const contentStarted = (contentRef.current?.getBoundingClientRect().top || Infinity) <= 0
  if (post.type === 'guide-post') {
    return (
      <div className={styles.blog}>
        <ReadProgress scrollPos={calculatedScrollPos} contentRef={contentRef} />
        <GuideHeader
          post={post}
          postConfig={postConfig}
          setHeight={setGuideHeight}
          published={dayjs(post.published_at).format('MMM D, YYYY')}
        />
        <div className={styles.wrapper}>
          <ContentsTool post={post} postConfig={postConfig} scrollPos={calculatedScrollPos} hidden={!contentStarted} />
          <div className={styles.container}>
            <div className={styles.content} data-guide={true}>
              <div ref={contentRef}>
                <ContentsTool post={post} postConfig={postConfig} scrollPos={calculatedScrollPos} staticMode />
                <div dangerouslySetInnerHTML={{ __html: post.html || '' }} />
              </div>
            </div>
            <GuideNavigator post={post} />
          </div>
          <BlogBanner hidden={!contentStarted} />
        </div>
        <GuideFooter post={post} />
        <div className={`blog-post-comments ${styles.comments}`} id='blog-post-comments'></div>
        <IconsLoader post={post} />
      </div>
    )
  }
  return (
    <>
      <ReadProgress scrollPos={calculatedScrollPos} contentRef={contentRef} />
      <div className={styles.wrapper}>
        <ContentsTool post={post} postConfig={postConfig} scrollPos={calculatedScrollPos} />
        <div className={styles.container}>
          <div className={styles.content}>
            <div ref={contentRef}>
              <div className={styles.header}>
                <div className={styles.headerContent}>
                  <div className={styles.headerChip}>
                    <CalendarIcon />
                    {dayjs(post.published_at).format('MMM D, YYYY')}
                  </div>
                  <Link className={styles.headerChip} href='#blog-post-comments'>
                    <CommentIcon />
                  </Link>
                </div>
              </div>
              <h1 className={styles.title}>{post.title}</h1>
              <div className={styles.imageContainer}>
                <Image layout='fill' src={post.feature_image || ''} alt={post.title || ''} objectFit='contain' />
              </div>
              <div dangerouslySetInnerHTML={{ __html: post.html || '' }} />
            </div>
            <div className='blog-post-comments' id='blog-post-comments'></div>
          </div>
        </div>
        <BlogBanner />
      </div>
    </>
  )
}

export default BlogPostContainer
